import React, { useRef, useCallback, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { NumberInput } from '@/components/ui';
import { Button } from '@/components/ui';
import { Plus, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { EASE_EXPO_OUT, DURATION_MEDIUM } from '@/lib/motion';

const getOrdinal = (n: number) => {
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

interface FeeScheduleInputProps {
  fixedFeeSchedule: Array<{ fee_amount: number | null }> | null;
  onFeeScheduleChange: (newSchedule: Array<{ fee_amount: number | null }>) => void;
  columns?: 2 | 3;
}

export const FeeScheduleInput: React.FC<FeeScheduleInputProps> = ({
  fixedFeeSchedule,
  onFeeScheduleChange,
  columns = 2,
}) => {
  const fees = useMemo(() => fixedFeeSchedule ?? [], [fixedFeeSchedule]);
  const feeFieldWidthClass = columns === 3 ? 'w-30' : 'w-32';

  // ── Stable row IDs for AnimatePresence keying ──
  const idsRef = useRef<string[]>([]);
  const nextIdRef = useRef(0);

  const rowIds = useMemo(() => {
    const current = idsRef.current;
    const len = fees.length;
    if (current.length < len) {
      while (current.length < len) {
        current.push(`fee-${nextIdRef.current++}`);
      }
    } else if (current.length > len) {
      idsRef.current = current.slice(0, len);
    }
    return idsRef.current;
  }, [fees]);

  // ── Auto-focus tracking for newly added rows ──
  const pendingFocusRef = useRef(false);

  const items = fees.map((f, i) => ({
    index: i,
    fee_amount: f.fee_amount,
  }));

  const handleFeeChange = useCallback(
    (index: number, value: number | null) => {
      const newSchedule = [...fees];
      newSchedule[index] = { fee_amount: value };
      onFeeScheduleChange(newSchedule);
    },
    [fees, onFeeScheduleChange],
  );

  const handleRemove = useCallback(
    (index: number) => {
      idsRef.current = idsRef.current.filter((_, i) => i !== index);
      const newSchedule = fees.filter((_, i) => i !== index);
      onFeeScheduleChange(newSchedule);
    },
    [fees, onFeeScheduleChange],
  );

  const hasInvalidFee = fees.some(
    (f) => f.fee_amount == null || isNaN(f.fee_amount) || f.fee_amount < 0.01
  );

  const handleAdd = useCallback(() => {
    if (hasInvalidFee) return;
    pendingFocusRef.current = true;
    onFeeScheduleChange([...fees, { fee_amount: null }]);
  }, [fees, onFeeScheduleChange, hasInvalidFee]);

  const renderItem = (item: { index: number; fee_amount: number | null }) => (
    <motion.div
      key={rowIds[item.index]}
      layout
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: DURATION_MEDIUM, ease: EASE_EXPO_OUT }}
      className={cn(
        "flex items-center justify-start sm:justify-end gap-2",
        columns === 2 && fees.length === 1 && item.index === 0 && "col-start-2",
      )}
    >
      <span className="font-label text-[10px] sm:text-[12px] text-muted-foreground w-6 sm:w-8 text-left sm:text-right shrink-0">
        {getOrdinal(item.index + 1)}
      </span>
      <div className={cn("relative", feeFieldWidthClass)}>
        <NumberInput
          ref={(el) => {
            if (el && item.index === fees.length - 1 && pendingFocusRef.current) {
              pendingFocusRef.current = false;
              requestAnimationFrame(() => el.focus());
            }
          }}
          aria-label={`${getOrdinal(item.index + 1)} fee amount`}
          className="w-full min-w-0 [&_.number-input-inner]:pr-8"
          size="xs"
          touchTarget
          unit="$"
          unitPosition="left"
          step="0.01"
          min="0.01"
          placeholder="0.00"
          value={item.fee_amount ?? ''}
          onChange={(e) => {
            const val = e.target.value;
            handleFeeChange(item.index, val === '' ? null : parseFloat(val));
          }}
        />
        <button
          type="button"
          onClick={() => handleRemove(item.index)}
          aria-label={`Remove ${getOrdinal(item.index + 1)} fee`}
          className="absolute right-2 top-1/2 -translate-y-1/2 shrink-0 text-muted-foreground hover:text-destructive transition-colors cursor-pointer touch-target"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </motion.div>
  );

  // Empty state — just the Add Fee button
  if (fees.length === 0) {
    return (
      <div className="flex items-center justify-end">
        <Button
          variant="secondary"
          size="sm"
          onClick={handleAdd}
          aria-label="Add Fee"
          className={cn(
            "group flex items-center justify-center gap-2 cursor-pointer touch-feedback touch-target",
            feeFieldWidthClass,
          )}
        >
          <Plus className="h-4 w-4 transition-transform duration-200 ease-out group-hover:rotate-90 group-active:scale-90" />
          <span className="transition-transform duration-200 ease-out group-hover:translate-x-0.5">
            Add Fee
          </span>
        </Button>
      </div>
    );
  }

  // Populated state — grid is the ONLY child (no Add Fee button here).
  // Add Fee button is rendered by the parent below the grid row.
  return (
    <>
      {/* Fee grid */}
      <div
        className={cn(
          "grid justify-items-start sm:justify-items-end gap-x-3 gap-y-2",
          columns === 3 ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-2",
        )}
      >
        <AnimatePresence initial={false}>
          {items.map(renderItem)}
        </AnimatePresence>

        {/* 3-col mode: Add Fee flows into the next grid cell beside the last fee */}
        {columns === 3 && (
          <motion.div
            key="add-fee-inline"
            layout
            className="flex items-center justify-start sm:justify-end gap-2"
          >
            <span className="font-label text-[10px] sm:text-[12px] text-muted-foreground w-6 sm:w-8 text-left sm:text-right shrink-0">
              {getOrdinal(fees.length + 1)}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleAdd}
              disabled={hasInvalidFee}
              aria-label="Add Fee"
              className={cn(
                "group flex items-center justify-center gap-2 cursor-pointer touch-feedback touch-target",
                feeFieldWidthClass,
              )}
            >
              <Plus className="h-4 w-4 transition-transform duration-200 ease-out group-hover:rotate-90 group-active:scale-90" />
              <span className="transition-transform duration-200 ease-out group-hover:translate-x-0.5">
                Add Fee
              </span>
            </Button>
          </motion.div>
        )}
      </div>

      {/* 2-col mode: Add Fee on its own full-width row below */}
      {columns === 2 && (
        <div className="md:col-span-2 flex justify-start sm:justify-end">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleAdd}
            disabled={hasInvalidFee}
            aria-label="Add Fee"
            className={cn(
              "group flex items-center justify-center gap-2 cursor-pointer touch-feedback touch-target",
              feeFieldWidthClass,
            )}
          >
            <Plus className="h-4 w-4 transition-transform duration-200 ease-out group-hover:rotate-90 group-active:scale-90" />
            <span className="transition-transform duration-200 ease-out group-hover:translate-x-0.5">
              Add Fee
            </span>
          </Button>
        </div>
      )}
    </>
  );
};
